import { createSlice } from '@reduxjs/toolkit';

// Matched by action type string on purpose. Importing updateCustomerBalanceThunk from
// 'store/customer' closes an import cycle (store/reducer → this slice → store/customer →
// customer/thunks → api/clients → store) that leaves the thunk in its temporal dead zone
// while combineReducers builds this slice — the whole SPA then dies at startup with
// "Cannot access 'X' before initialization" and a blank page.
const UPDATE_CUSTOMER_BALANCE_FULFILLED =
  'customer/updateCustomerBalanceThunk/fulfilled';

const initialState = {
  transactions: [],
  specificTransactions: [],
  paymentRequests: [],
  // Set on first successful fetch — lets consumer pages fetch on demand exactly once per
  // session (these feeds are no longer preloaded at app startup; see App.js).
  paymentRequestsLoaded: false,
  transactionsLoaded: false,
  specificTransactionsLoaded: false,
  error: null,
  isLoading: false
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    // General loading state for any request
    requestStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    // General failure state for any request
    requestFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    // Fetch payment requests success
    fetchPaymentRequestsSuccess: (state, action) => {
      state.paymentRequests = action.payload;
      state.paymentRequestsLoaded = true;
      state.isLoading = false;
    },
    // Fetch payment requests success
    createPaymentRequestSuccess: state => {
      state.isLoading = false;
    },
    // Fetch user transactions success
    fetchUserTransactionSuccess: (state, action) => {
      state.transactions = action.payload;
      state.transactionsLoaded = true;
      state.isLoading = false;
    },
    // Fetch user specific transactions success
    fetchUserSpecificTransactionSuccess: (state, action) => {
      state.specificTransactions = action.payload;
      state.specificTransactionsLoaded = true;
      state.isLoading = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(UPDATE_CUSTOMER_BALANCE_FULFILLED, (state, action) => {
      const res = action.payload || {};
      const { id, formData } = action.meta.arg || {};

      const tx = {
        id:
          res.transactionId || (res.transaction && res.transaction.id) || null,
        date: res.date || new Date().toISOString(),
        amount: formData.isDeduction
          ? -Math.abs(formData.amount)
          : Number(formData.amount),
        orderId: res.transactionId,
        userId: id,
        email: undefined,
        description: formData.description || ''
      };

      state.specificTransactions = [tx, ...(state.specificTransactions || [])];
      state.isLoading = false;
    });
  }
});

export const {
  requestStart,
  requestFailure,
  fetchUserTransactionSuccess,
  fetchPaymentRequestsSuccess,
  createPaymentRequestSuccess,
  fetchUserSpecificTransactionSuccess
} = transactionsSlice.actions;

export default transactionsSlice.reducer;
